import React from 'react';
import { Trash2, Copy } from 'lucide-react';

export interface ThemePreset {
  id: string;
  name: string;
  primaryColor: string | null;
  secondaryColor: string | null;
  accentColor: string | null;
  successColor: string | null;
  warningColor: string | null;
  errorColor: string | null;
  infoColor: string | null;
  grayColor: string | null;
  headingFontFamily: string | null;
  bodyFontFamily: string | null;
  borderRadius: string | null;
  darkModeEnabled: boolean;
}

interface ThemePresetCardProps {
  preset: ThemePreset;
  isActive?: boolean;
  onEdit: (preset: ThemePreset) => void;
  onDelete?: (presetId: string) => void;
  onDuplicate?: (preset: ThemePreset) => void;
  /** Hide the duplicate/delete overlay (read-only picker for org admins). */
  hideActions?: boolean;
}

const COLOR_KEYS = [
  'primaryColor', 'secondaryColor', 'accentColor',
  'successColor', 'warningColor', 'errorColor', 'infoColor', 'grayColor',
] as const;

export function ThemePresetCard({ preset, isActive, onEdit, onDelete, onDuplicate, hideActions }: ThemePresetCardProps) {
  const isDefault = preset.id === 'default';

  return (
    <div
      className={`relative rounded-xl border p-4 transition-all cursor-pointer group hover:shadow-md ${
        isActive
          ? 'border-primary-500 ring-2 ring-primary-200 bg-primary-50/30'
          : 'border-border bg-background hover:border-primary-300'
      }`}
      onClick={() => onEdit(preset)}
      data-testid={isActive ? 'theme-card-active' : 'theme-card'}
    >
      {/* AC-Design-3: pills are in normal flow (not absolutely positioned at
          -top-2) so they never overlap the headline. Each pill occupies its
          own row with a bottom margin that creates a clear vertical gap. */}
      {(isDefault || isActive) && (
        <div className="flex gap-1.5 mb-2">
          {isDefault && (
            <span className="px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider bg-primary-500 text-white rounded-full">
              Default
            </span>
          )}
          {isActive && !isDefault && (
            <span className="px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider bg-success-500 text-white rounded-full">
              Active
            </span>
          )}
        </div>
      )}

      <h3 className="text-sm font-semibold text-foreground mb-3 pr-16 truncate">{preset.name}</h3>

      <div className="flex gap-1.5 mb-3">
        {COLOR_KEYS.map((key) => {
          const color = preset[key] as string | null;
          return (
            <div
              key={key}
              className="w-6 h-6 rounded-full border border-black/10 shrink-0"
              style={{ backgroundColor: color || '#e5e7eb' }}
              title={`${key}: ${color || 'unset'}`}
            />
          );
        })}
      </div>

      <div className="text-xs text-muted-foreground space-y-0.5">
        {preset.headingFontFamily && (
          <p><span className="font-medium">H:</span> {preset.headingFontFamily}</p>
        )}
        {preset.bodyFontFamily && (
          <p><span className="font-medium">B:</span> {preset.bodyFontFamily}</p>
        )}
        {preset.borderRadius && (
          <p><span className="font-medium">Radius:</span> {preset.borderRadius}</p>
        )}
      </div>

      {!hideActions && (
        <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          {onDuplicate && (
            <button
              onClick={(e) => { e.stopPropagation(); onDuplicate(preset); }}
              className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              title="Duplicate"
            >
              <Copy size={14} />
            </button>
          )}
          {onDelete && !isDefault && (
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(preset.id); }}
              className="p-1.5 rounded-lg text-muted-foreground hover:text-error-600 hover:bg-error-50 transition-colors"
              title="Delete"
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
